import { Maybe, RemoteData } from '@passionware/monads';
import { Resource } from '__types__/v1/models';
import {
  ResourceListResponse,
  ResourceQuery
} from '../../../api/resource/resource.api';
import { RemoteInfiniteData } from '../../../platform/infiniteQuery';

export interface ResourceService {
  useList: (query: Maybe<ResourceQuery>) => RemoteData<ResourceListResponse>;
  /**
   * One list read page by page, each of the query's page size: the first on mount, the next on
   * `fetchNextPage` while `hasNextPage`. An edited query keeps the previous pages on show until
   * its first page comes. Invalidated with `useList`.
   */
  useListInfinite: (
    query: Maybe<ResourceQuery>
  ) => RemoteInfiniteData<ResourceListResponse>;
  /**
   * Pages of one list, one result per page index asked for, in the same order: a continuous list
   * reads the pages its visible rows fall on. Shares its cache with `useList`.
   */
  useListPages: (
    query: Maybe<ResourceQuery>,
    pageIndices: number[],
    pageSize: number
  ) => RemoteData<ResourceListResponse>[];
  ensureList: (query: ResourceQuery) => Promise<ResourceListResponse>;
  useItem: (id: Maybe<Resource['id']>) => RemoteData<Resource>;
  ensureItem: (id: Resource['id']) => Promise<Resource>;
  useItemsById: (ids: Resource['id'][]) => RemoteData<Resource[]>;
}

/** An item read the server answered without the resource: it does not exist or the user cannot see it. */
export class ResourceNotFoundError extends Error {
  constructor(resourceId: Resource['id']) {
    super(`Resource with id ${resourceId} not found`);
    this.name = 'ResourceNotFoundError';
  }
}

export interface WithResourceService {
  resourceService: ResourceService;
}
